import React, {useState, useEffect, useCallback, ChangeEvent, ProgressHTMLAttributes, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import Video from '@src/ds/components/molecules/Video/Video';
import Hls from 'hls.js';
import EmbedStyl from './Embed.styl';
import useSupport from './hooks/Support';
import FullScreen from './components/FullScreen';
import Pip from './components/Pip';
import ChromeCast from './components/ChromeCast';
import AirPlay from './components/AirPlay';
import LogoSmartplayer from './components/LogoSmartplayer';
import AIButton from './components/AIButton';
import Configuration from './components/Configuration';
import Volume from './components/Volume';
import {AIProductsResponse} from '@src/Data/Model/AIProducts';
import AIProducsService from '@src/Data/DataSource/AIProducts';
import MindMap from '@src/ds/components/molecules/MindMap/MindMap';
import SummaryText from '@src/ds/components/molecules/SummaryText/SummaryText';
import Quiz from '@src/ds/components/molecules/Quiz/Quiz';
import Search from '@src/ds/components/molecules/Search/SearchComponent';
import SwitchSummary from './components/SwitchSummary';
import SearchButton from './components/SearchButton';
import ClickableArea from './components/ClickableArea';
import Seekbar from './components/Seekbar';
import PlayButton from './components/PlayButton';
import Loader from '@src/ds/components/atoms/Loader';

// import {VideoResponse} from '@src/Data/Model/Video';
// import DataSourceMain from '@src/Data/DataSource/Video';
// import HttpClient from '@Infra/Http';
// import Loader from '@src/ds/components/atoms/Loader';
// import {PageLoader} from '@src/ds/components/atoms/Loader/Loader.styl';

export type Embed2Props = {
  code?: string;
};

export type Level = {
  index: number;
  id: string;
  bitrate: number;
  width: number;
  height: number;
};

export type StreamLevels = Array<Level>;


const Embed2 = ({ code }: Embed2Props) => {
  const supported = useSupport();
  const [isBuffering, setIsBuffering] = React.useState<boolean>(true);
  const [currentLevel, setCurrentLevel] = React.useState<number>(0);
  const [levels, setLevels] = React.useState<StreamLevels>([]);

  const [duration, setDuration] = React.useState<number>(0);
  const videoEl = React.useRef(null);
  const videoContainer = React.useRef<HTMLDivElement | null>(null);
  const [openSummary, setOpenSummary] = React.useState<boolean>(false);
  const [openSummaryText, setOpenSummaryText] = React.useState<boolean>(false);
  const [openMindMap, setOpenMindMap] = React.useState<boolean>(false);
  const [openQuiz, setOpenQuiz] = React.useState<boolean>(false);
  const [openSearch, setOpenSearch] = React.useState<boolean>(false);
  const [aiProducts, setAIProducts] = React.useState<AIProductsResponse | null>(null);

  const [data, setData] = useState<{
    primaryColor: string;
    textColor: string;
    transcription: string;
    subtitles: {
      label: string;
      src: string;
      language: string;
    }[];
    config: {
      progressBar: boolean;
      showConfigurations: boolean;
      volume: boolean;
      logo: boolean;
      keepWatching: boolean;
      airplay: boolean;
      chromecast: boolean;
      bigPlay: boolean;
      autoplay: boolean;
      fullScreen: boolean;
      muted: boolean;
      responsive: boolean;
      preload: boolean;
      loop: boolean;
      pip: boolean;
      controls: boolean;
      originalAspectRatio: boolean;
    };
    aspectRatio: string;
    poster: string;
    video: string;
    token: string;
    cdnKey: string;
    summaryVideo: string;
    summaryVideoCdnKey: string;
    authorization: string;
    waterMark: string;
    ai: string;
    code: string;
    name: string;
  } | null>(null);
  const [hls, setHls] = React.useState<any>(null);

  // if (isLoading) {
  //   return (
  //     <PageLoader className="page-loader--studio">
  //       <Loader model="inline" variant="blue" />
  //     </PageLoader>
  //   );
  // }

  // <Video
  //   id="embed-player"
  //   primaryColor={data?.primaryColor || ''}
  //   transcription={data?.transcription || ''}
  //   subtitles={data?.subtitles || []}
  //   config={data?.config || {
  //     autoplay: false,
  //     fullScreen: false,
  //     muted: false,
  //     responsive: true,
  //     preload: true,
  //     loop: false,
  //     pip: true,
  //     controls: true,
  //     originalAspectRatio: false,
  //   }}
  //   aspectRatio={data?.aspectRatio || '16:9'}
  //   src={data?.video || ''}
  //   poster={data?.poster || ''}
  //   token={data?.token || ''}
  //   cdnKey={data?.cdnKey || ''}
  //   summaryVideo={data?.summaryVideo || ''}
  //   summaryVideoCdnKey={data?.summaryVideoCdnKey || ''}
  //   mediaCode={id || ''}
  //   ai={(data?.ai || '')}
  //   hasSearch={(data?.ai || '').indexOf('SMART_SEARCH') > -1}
  //   waterMark={data?.waterMark}
  //   authorization={data?.authorization || ''}
  // />

  function getColor(hexColor: string) {
    hexColor = hexColor.replace('#', '');
    const r = parseInt(hexColor.substring(0,2),16);
    const g = parseInt(hexColor.substring(2,2),16);
    const b = parseInt(hexColor.substring(4,2),16);
    const yiq = ((r*299)+(g*587)+(b*114))/1000;
    return (yiq >= 128) ? '#000' : '#fff';
  }

  async function getSource() {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    const dataAttr = rootElement.getAttribute('data');
    // const worker = rootParams.getAttribute('data-worker');
    // const ssrVideo = rootParams.getAttribute('data-video');
    // const posterVideo = rootParams.getAttribute('data-poster');
    // const tokenVideo = rootParams.getAttribute('data-id');

    // console.log('worker', worker, 'hasServiceWorker', hasServiceWorker);

    // setWorker(worker === '1');
    if (dataAttr && dataAttr !== '[DATA]') {
      const newData = JSON.parse(dataAttr.split("'").join('"') || '');
      const aspectRatio = newData?.originalAspectRatio ? newData?.aspectRatio : '16/9';
      rootElement.style.aspectRatio = (aspectRatio).toString();
      rootElement.style.setProperty('--aspect-ratio', (aspectRatio).toString().replace(':', '/'));
      rootElement.style.setProperty('--primary-color', (data?.primaryColor || '#444CE7').toString());
      rootElement.style.setProperty('--text-color', (getColor(data?.primaryColor || '')).toString());

      // newData.config.autoplay = true;
      // newData.config.muted = true;
      setData({
        ...newData,
        textColor: getColor(newData?.primaryColor),
      });
      // setIsLoading(false);
      // return null;
    }

    // if (dataAttr
    // && dataAttr !== '[DATA]'
    // && hasServiceWorker
    // && worker === '1') {
    //   setIsLoading(false);
    // }

    // if (!hasServiceWorker || worker === '0') {
    //   setIsLoading(false);
    // }

    // const referrer = document?.referrer ? new URL(document?.referrer) : null;

    // const response: VideoResponse = await dataSource.getMedia({
    //   mediaCode: id,
    //   code: referrer?.origin ? window.btoa(referrer?.origin) : window.btoa('https://player.scaleup.com.br'),
    // });

    // setPoster(response.success ? (response?.poster || '') : '');
    // setSource(response.success ? response?.video : '');
    // setIsLoading(false);

    // return null;
  }

  async function onLoadAI() {
    if (!data?.code || !data?.authorization) {
      return;
    }

    if (data?.ai === '' || data?.ai === null) {
      return;
    }

    const result = await AIProducsService({
      mediaCode: data?.code,
      authorization: data?.authorization,
      products: (data?.ai || '').split(',').filter((item) => ['MIND_MAP', 'SUMMARY', 'SUMMARY_TEXT', 'QUESTION'].includes(item)),
    });

    if (result.success) {
      setAIProducts(result);
    }
  }

  useEffect(() => {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

    const loadEvent = () => {
      getSource();
    };

    rootElement.addEventListener('load', loadEvent, false);

    return () => {
      rootElement.removeEventListener('load', loadEvent);
    }
  }, [code]);

  useEffect(() => {
    // if (hasServiceWorker && navigator.serviceWorker?.controller?.state === 'activated') {
    getSource();
    // }
  }, []);


  const isPlayerBuffering = useCallback(() => {
    console.log('isPlayerBuffering');

    if (!isBuffering) {
      setIsBuffering(true);
    }
  }, [isBuffering])

  const isPlayerNotBuffering = useCallback(() => {
    console.log('isPlayerNotBuffering');

    if (isBuffering) {
      setIsBuffering(false);
    }
  }, [isBuffering])

  useEffect(() => {
    if (!hls || !hls.media) {
      return;
    }

    hls.media.addEventListener('waiting', isPlayerBuffering);

    hls.media.addEventListener('playing', isPlayerNotBuffering);

    hls.media.addEventListener('canplay', isPlayerNotBuffering);

    hls.media.addEventListener('seeked', isPlayerBuffering);

    return () => {
      hls.media.removeEventListener('waiting', isPlayerBuffering);

      hls.media.removeEventListener('playing', isPlayerNotBuffering);

      hls.media.removeEventListener('canplay', isPlayerNotBuffering);

      hls.media.removeEventListener('seeked', isPlayerBuffering);
    }
  }, [hls, hls?.media, isBuffering, isPlayerBuffering, isPlayerNotBuffering]);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (!hls) {
      setHls(new Hls({
        autoStartLoad: true,
        backBufferLength: 90,
        debug: false,
        enableWorker: true,
        enableWebVTT: true,
        lowLatencyMode: true,
        maxBufferLength: 60,
        maxMaxBufferLength: 60,
        frontBufferFlushThreshold: 30,
        // preferManagedMediaSource: false,

        // renderTextTracksNatively: false,
        xhrSetup: (xhr: any, url: string) => {
          if ((url).toString().indexOf('https://stream.scaleup.com.br/player/v1/playlists') > -1) {
            xhr.setRequestHeader('Authorization', 'Bearer '+ (data?.authorization || ''));
          }
        },
      }));

      return;
    }
  }, [data]);

  useEffect(() => {
    if (!hls) {
      return;
    }

    if (!videoEl.current || !videoContainer?.current) {
      return;
    }

    hls.attachMedia(videoEl.current);

    hls.on(Hls.Events.MEDIA_ATTACHED, (event: any) => {
      if (!videoContainer?.current) {
        return;
      }

      console.log('MEDIA_ATTACHED', event);

      hls.loadSource(hls.url ? hls.url : data?.video);

      const newLevels = hls.levels.map((level: any, index: number) => ({
        key: index,
        id: level.id,
        ...level.attrs,
        height: level.height,
      }));

      setLevels(newLevels);
    });

    hls.on(Hls.Events.LEVEL_LOADED, (eventType: any, eventValues: any) => {
      console.log('LEVEL_LOADED');

      setDuration(eventValues.details.totalduration);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      console.log('MANIFEST_PARSED');

      const newLevels = hls.levels.map((level: any, index: number) => ({
        key: index,
        id: level.id,
        ...level.attrs,
        height: level.height,
        bitrate: level.bitrate,
      }));

      setData({
        ...data,
        subtitles: hls.allSubtitleTracks.map((subtitle: any) => ({
          label: subtitle.name,
          src: subtitle.url,
          language: subtitle.lang,
        })),
      } as typeof data);

      setLevels(newLevels);
    });

    hls.on(Hls.Events.LEVEL_SWITCHING, function (eventName: string, data: any) {
      console.log('LEVEL_SWITCHING');

      if (data.level === -1) {
        return;
      }

      console.log('currentLevel', data.level);

      setCurrentLevel(data.level);
	  });

    hls.media.addEventListener('play', () => {
      if (videoContainer?.current) {
        videoContainer?.current.classList.remove('smartplayer--ended');
        videoContainer?.current.classList.remove('smartplayer--paused');
        videoContainer?.current.classList.add('smartplayer--playing');
      }
    });

    hls.media.addEventListener('pause', () => {
      if (videoContainer?.current) {
        videoContainer?.current.classList.remove('smartplayer--ended');
        videoContainer?.current.classList.remove('smartplayer--playing');
        videoContainer?.current.classList.add('smartplayer--paused');
      }
    });

    hls.media.addEventListener('ended', () => {
      if (videoContainer?.current) {
        videoContainer?.current.classList.remove('smartplayer--playing');
        videoContainer?.current.classList.remove('smartplayer--paused');
        videoContainer?.current.classList.add('smartplayer--ended');
      }
    });

    if (data?.summaryVideo) {
      videoContainer?.current.classList.add('smartplayer--video-with-summary');
    } else {
      videoContainer?.current.classList.add('smartplayer--video-without-summary');
    }

    if (data?.config?.autoplay) {
      try {
        hls.media.play().then(() => {
          if (videoContainer?.current) {
            videoContainer?.current.classList.add('smartplayer--playing');
          }
        }).catch(() => {
          if (videoContainer?.current) {
            videoContainer?.current.classList.add('smartplayer--paused');
          }

          hls.media.volume = 0;
          hls.media.muted = true;
          hls.media.play();
        });
      } catch (error) {
        try {
          hls.media.volume = 0;
          hls.media.muted = true;
          hls.media.play();
        } catch (anotherError) {
          videoContainer?.current.classList.add('smartplayer--paused');
        }
      }
    } else if (videoContainer?.current) {
      videoContainer?.current.classList.add('smartplayer--paused');
    }

    return () => {
      if (hls) {
        hls.media.removeEventListener('play');
        hls.media.removeEventListener('paused');
        hls.media.removeEventListener('ended');
        hls.destroy();
      }

      setHls(undefined);
    };

  }, [hls]);

  /*****
  ***PLAY BUTTON
  *******/
  const isVideoPlaying = () => {
    return !!(hls.media.currentTime > 0 && !hls.media.paused && !hls.media.ended && hls.media.readyState > 2);
  };

  const playVideo = () => {
    if (isVideoPlaying()) {
      hls.media.pause();
      return;
    }

    if (hls.media.ended) {
      hls.media.currentTime = 0;
    }

    hls.media.play();
  };

  const onBackdropClick = (event: any) => {
    const footer = event?.target?.parentElement;

    if (!footer) {
      return;
    }

    const configurationBtn = footer.querySelector('.smartplayer__configuration');

    configurationBtn?.classList.remove('smartplayer__configuration--opened');
    configurationBtn?.classList.remove('smartplayer__quality--opened');
    configurationBtn?.classList.remove('smartplayer__velocity--opened');
    configurationBtn?.classList.remove('smartplayer__subtitle--opened');

    const aiButton = footer.querySelector('.smartplayer__ai');

    aiButton?.classList.remove('smartplayer__ai--opened');

    // footer.classList.remove('smartplayer__footer--dropdown');

    const volumeButton = footer.querySelector('.smartplayer__volume') as HTMLButtonElement;
    volumeButton?.classList.remove('smartplayer__volume--opened');

    setOpenMindMap(false);
    setOpenSummary(false);
    setOpenSummaryText(false);
    setOpenQuiz(false);
    setOpenSearch(false);
  }

  const videoElement = useMemo(() => (
    <>
      <video key={data?.code} ref={videoEl} poster={data?.poster} crossOrigin="anonymous" x-webkit-airplay="allow" preload='auto' playsInline controlsList="nodownload" tabIndex={-1} muted={data?.config?.muted} loop={data?.config?.loop}>
        <source src={`${data?.video}&authorization=${data?.authorization || ''}`} type="application/x-mpegURL"></source>
        {/* {(data?.subtitles || []).map((subTitle) => (
          <track id={`subtitle--${subTitle.language}`} kind="metadata" src={subTitle.src} srcLang={subTitle.language} label={subTitle.label} key={subTitle.language} />
        ))} */}
      </video>
      {/* <video crossOrigin="anonymous" x-webkit-airplay="allow" playsInline controlsList="nodownload" tabIndex={-1} style={{opacity: 0, width: 0, height: 0}} className="video--native" poster={data?.poster}>
        <source src={data?.video} type="application/x-mpegURL"></source>
        {(data?.subtitles || []).map((subTitle) => (
          <track id={`subtitle--${subTitle.language}`} kind="subtitles" src={subTitle.src} srcLang={subTitle.language} label={subTitle.label} key={subTitle.language} />
        ))}
      </video> */}
    </>
  ), [videoEl, data]);

  return (
    <>
      <EmbedStyl className="smartplayer" ref={videoContainer} primaryColor={data?.primaryColor} textColor={data?.textColor}>
        <div className="smartplayer__container">
          <div className={`smartplayer__video ${isBuffering ? 'smartplayer--loading' : 'smartplayer--not-loading'}`}>
            {
              currentLevel != null && levels != null && levels[currentLevel] ?
              (
                <div className="smartplayer__videoResolution">
                  {
                    `${levels[currentLevel]?.height}p`
                  }
                </div>
              ) : null
            }
            {
              videoElement
            }
            <div className="smartplayer__loader">
              <Loader
                model="round"
                variant="currentcolor"
              />
            </div>
          </div>
          <div className="smartplayer__front">
            <ClickableArea
              hls={hls}
              videoContainer={videoContainer}
              code={data?.code || ''}
              keepWatching={!!data?.config?.keepWatching}
              autoplay={!!data?.config?.autoplay}
              bigplay={!!data?.config?.bigPlay}
              playVideo={playVideo}
            />
            <div dir="auto" className="smartplayer__captions">
            </div>
            <div className="smartplayer__footer">
              <div className="smartplayer__footerBackDrop" onClick={onBackdropClick}></div>
              <PlayButton
                code={data?.code || ''}
                onClick={playVideo}
              />
              <SwitchSummary
                enabled={!!(data?.ai && data?.ai?.indexOf('SUMMARY') > -1)}
                hls={hls}
                code={data?.code || ''}
                primaryColor={data?.primaryColor || ''}
                textColor={data?.textColor || ''}
                videoUrl={data?.video || ''}
                summaryVideoUrl={data?.summaryVideo || ''}
              />
              <div className="smartplayer__controlbar">
                <Volume
                  enabled={!!data?.config?.volume}
                  hls={hls}
                  code={data?.code || ''}
                />
                <Seekbar
                  enabled={!!data?.config?.progressBar}
                  hls={hls}
                  code={data?.code || ''}
                  duration={duration}
                  videoContainer={videoContainer}
                />
                <SearchButton
                  enabled={!!(data?.ai && data?.ai?.indexOf('SMART_SEARCH') > -1)}
                  aiProducts={data?.ai || ''}
                  code={data?.code || ''}
                  onClick={() => {
                    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

                    // if (!openSearch) {
                    // rootElement.querySelector('.smartplayer__footer')?.classList.add('smartplayer__footer--dropdown');
                    // } else {
                    // rootElement.querySelector('.smartplayer__footer')?.classList.remove('smartplayer__footer--dropdown');
                    // }

                    setOpenSearch(!openSearch);
                    setOpenSummary(false);
                    setOpenSummaryText(false);
                    setOpenMindMap(false);
                    setOpenQuiz(false);
                  }}
                />
                {/* <div className="smartplayer__subtitle">
                  <button className="smartplayer__bar-button" type="button" onClick={(event) => {
                    event.currentTarget?.parentElement?.classList.toggle('smartplayer__subtitle--opened');
                  }} disabled={!supported.subtitles}>
                    <div className="icon icon--subtitle">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="13" viewBox="0 0 16 13" fill="none">
                        <rect y="0.279785" width="16" height="12.44" rx="2" fill="currentColor"></rect>
                        <path d="M2.64526 8.6816V4.31796H3.56785V7.92094H5.43859V8.6816H2.64526Z" fill="black"></path>
                        <path d="M5.92956 8.6816V4.31796H8.8699V5.07861H6.85215V6.11839H8.71862V6.87904H6.85215V7.92094H8.87842V8.6816H5.92956Z" fill="black"></path>
                        <path d="M12.3769 5.72847C12.347 5.62478 12.3051 5.53316 12.2511 5.45361C12.1972 5.37265 12.1311 5.30447 12.053 5.24907C11.9763 5.19225 11.8882 5.14893 11.7888 5.1191C11.6908 5.08927 11.5821 5.07435 11.4628 5.07435C11.2398 5.07435 11.0438 5.12975 10.8747 5.24055C10.7071 5.35134 10.5764 5.51256 10.4827 5.72421C10.3889 5.93444 10.3421 6.19154 10.3421 6.49552C10.3421 6.79949 10.3882 7.05802 10.4805 7.27108C10.5729 7.48415 10.7036 7.6468 10.8726 7.75901C11.0416 7.86981 11.2412 7.9252 11.4713 7.9252C11.6801 7.9252 11.8584 7.88827 12.0061 7.81441C12.1553 7.73912 12.2689 7.6333 12.347 7.49694C12.4266 7.36057 12.4663 7.19935 12.4663 7.01327L12.6538 7.04097H11.5288V6.34637H13.3548V6.89608C13.3548 7.27961 13.2739 7.60915 13.1119 7.88472C12.95 8.15887 12.727 8.37052 12.4429 8.51966C12.1588 8.66739 11.8335 8.74126 11.4671 8.74126C11.058 8.74126 10.6986 8.65106 10.3889 8.47066C10.0793 8.28884 9.83779 8.03103 9.6645 7.69722C9.49262 7.36199 9.40668 6.96427 9.40668 6.50404C9.40668 6.15035 9.45782 5.83501 9.56009 5.55802C9.66379 5.27961 9.80867 5.04381 9.99475 4.85063C10.1808 4.65745 10.3975 4.51043 10.6446 4.40958C10.8918 4.30873 11.1595 4.2583 11.4479 4.2583C11.695 4.2583 11.9252 4.29452 12.1382 4.36697C12.3513 4.43799 12.5402 4.53884 12.705 4.66952C12.8712 4.8002 13.0068 4.95574 13.1119 5.13614C13.2171 5.31512 13.2845 5.51256 13.3144 5.72847H12.3769Z" fill="black"></path>
                      </svg>
                    </div>
                  </button>
                </div> */}
                <AIButton
                  enabled={!!data?.ai}
                  aiProducts={data?.ai || ''}
                  code={data?.code || ''}
                  hasAIOpened={openSummaryText || openSummary || openMindMap || openQuiz || openSearch}
                  onLoadAI={onLoadAI}
                  openEbook={() => {
                    setOpenSummary(true);
                    setOpenSummaryText(false);
                    setOpenMindMap(false);
                    setOpenQuiz(false);
                    setOpenSearch(false);
                  }}
                  openMindMap={() => {
                    setOpenSummary(false);
                    setOpenSummaryText(false);
                    setOpenMindMap(true);
                    setOpenQuiz(false);
                    setOpenSearch(false);
                  }}
                  openQuiz={() => {
                    setOpenSummary(false);
                    setOpenSummaryText(false);
                    setOpenMindMap(false);
                    setOpenQuiz(true);
                    setOpenSearch(false);
                  }}
                  openSummaryText={() => {
                    setOpenSummary(false);
                    setOpenSummaryText(true);
                    setOpenMindMap(false);
                    setOpenQuiz(false);
                    setOpenSearch(false);
                  }}
                />
                <Configuration
                  enabled={!!data?.config?.showConfigurations}
                  hls={hls}
                  code={data?.code || ''}
                  levels={levels || []}
                  currentLevel={currentLevel}
                  subtitles={data?.subtitles || []}
                  hasAIOpened={openSummary || openMindMap || openQuiz || openSearch}
                />
                <AirPlay
                  authorization={data?.authorization || ''}
                  enabled={!!data?.config?.airplay}
                  hls={hls}
                  video={data?.video || ''}
                  // subtitles={data?.subtitles || []}
                  code={data?.code || ''}
                  videoName={data?.name || ''}
                  videoPoster={data?.poster || ''}
                  supported={supported}
                />
                <ChromeCast
                  authorization={data?.authorization || ''}
                  enabled={!!data?.config?.chromecast}
                  video={data?.video || ''}
                  // subtitles={data?.subtitles || []}
                  code={data?.code || ''}
                  videoName={data?.name || ''}
                  videoPoster={data?.poster || ''}
                  supported={supported}
                />
                <Pip
                  enabled={!!data?.config?.pip}
                  hls={hls}
                  code={data?.code || ''}
                  supported={supported}
                />
                <FullScreen
                  enabled={!!data?.config?.fullScreen}
                  code={data?.code || ''}
                  supported={supported}
                />
              </div>
              <LogoSmartplayer
                enabled={!!data?.config?.logo}
                code={data?.code || ''}
              />
            </div>
          </div>
        </div>
      </EmbedStyl>
      <div className="smartplayer__ai">
        <Search
          hls={hls}
          primaryColor={data?.primaryColor}
          textColor={data?.textColor || ''}
          mediaCode={data?.code || ''}
          authorization={data?.authorization}
          opened={openSearch} onClose={() => setOpenSearch(false)}
          hasAIOpened={openSummary || openMindMap || openQuiz || openSearch}
        />
        <MindMap
          text={aiProducts?.mindMap || ''}
          opened={openMindMap}
          onClose={() => setOpenMindMap(false)}
        />
        <SummaryText
          type="SUMMARY"
          mediaCode={code || ''}
          authorization={data?.authorization || ''}
          text={aiProducts?.summary || ''}
          opened={openSummary}
          onClose={() => setOpenSummary(false)}
        />
        <SummaryText
          type="SUMMARY_TEXT"
          text={aiProducts?.summaryText || ''}
          opened={openSummaryText}
          onClose={() => setOpenSummaryText(false)}
        />
        {
          (aiProducts?.questions || []).length > 0 ? (
            <Quiz
              questions={aiProducts?.questions || []}
              opened={openQuiz}
              onClose={() => setOpenQuiz(false)}
            />
          ) : null
        }
      </div>
    </>
  );
};

export default Embed2;
